import { FC, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormContext } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";

import Button from "components/v2/Button/Button";
import Checkbox from "components/v2/Checkbox/Checkbox";
import { ReactComponent as UploadIcon } from "assets/images/iconsV2/upload.svg";
import { deleteFile, uploadFile } from "api";
import { createPodBucketAttachment, deletePodBucketAttachment } from "api/services/shipment";
import { shipmentQueryKeys } from "api/queries/shipments/shipmentQueryKeys";
import { writePodBucketAttachmentsCache } from "api/queries/shipments/useGetPodBucketAttachments";
import errorToast from "helpers/errorToast";
import { formatFileSize } from "helpers/commonHelpers";
import UploadBar from "components/atoms/UploadBar/UploadBar";
import FilePreview from "components/v2/FilePreview/FilePreview";
import {
  attachmentMatchesBucket,
  isSyntheticOtherBucket,
  podBucketAssociatePayload,
} from "../podBucketAttachmentHelpers";

type BucketFile = {
  attachment_id: number | null;
  file_id: number;
  fileName: string;
  url: string;
  reference: string;
  size: number | null;
  bucket_name?: string;
};

type ProofOfDeliveryAttachmentItemProps = {
  bucket: any;
  dataSource: string;
  shipmentId: string;
  persistToPodBucket: boolean;
  isAttachmentReadOnly?: boolean;
  bucketFile?: BucketFile;
};

const ProofOfDeliveryAttachmentItem: FC<ProofOfDeliveryAttachmentItemProps> = function (props) {
  const { bucket, dataSource, shipmentId, persistToPodBucket, isAttachmentReadOnly = false, bucketFile } = props;

  const intl = useIntl();
  const queryClient = useQueryClient();
  const {
    getValues,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { isSubmitted },
  } = useFormContext();
  const watchedFileAttachments = watch("file_attachments");
  const fileAttachments = watchedFileAttachments ?? [];
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const hiddenFileInput = useRef<HTMLInputElement>(null);
  const attachmentFieldName = `selected_attachments.bucket-${bucket.id}`;
  const showAttachmentUploader = watch(attachmentFieldName);
  const isBucketMandatory = dataSource === "pod" && bucket.is_mandatory;

  const selectedFile = useMemo(
    () =>
      bucketFile ??
      fileAttachments.find((attachment: BucketFile) => attachmentMatchesBucket(attachment, bucket)),
    [bucketFile, fileAttachments, bucket],
  );

  const showAttachmentSection = Boolean(showAttachmentUploader || selectedFile);

  const invalidatePodBucketAttachments = () => {
    queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.podBucketAttachments(shipmentId) });
  };

  useEffect(() => {
    if (isBucketMandatory && !getValues(attachmentFieldName)) {
      setValue(attachmentFieldName, true);
      return;
    }
    if (selectedFile && !showAttachmentUploader) {
      setValue(attachmentFieldName, true);
    }
  }, [attachmentFieldName, isBucketMandatory, selectedFile, showAttachmentUploader, bucket]);

  useEffect(() => {
    if (!selectedFile && showAttachmentUploader && isSubmitted && !uploadProgress) {
      setError(`file_attachment-${bucket.id}`, { type: "required" });
    } else {
      clearErrors(`file_attachment-${bucket.id}`);
    }
  }, [selectedFile, showAttachmentUploader, isSubmitted, uploadProgress]);

  const handleUploadClick = () => hiddenFileInput?.current?.click();

  const handleUploadChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;
    const file = event.target.files[0];
    if (file) {
      uploadFileHandler(file);
    }
  };

  const handleAssociateAfterUpload = async (formattedFile: {
    attachment_id: number | null;
    file_id: number;
    fileName: string;
    url: string;
    reference: string;
    size: number;
    bucket_name?: string;
  }) => {
    if (!persistToPodBucket) {
      setUploadProgress(0);
      setValue("file_attachments", [...(getValues("file_attachments") ?? []), formattedFile]);
      return;
    }

    try {
      const { data } = await createPodBucketAttachment(
        shipmentId,
        podBucketAssociatePayload(formattedFile.file_id, bucket),
      );
      setUploadProgress(0);
      setValue("file_attachments", [
        ...(getValues("file_attachments") ?? []),
        {
          ...formattedFile,
          attachment_id: data.attachment_id,
          fileName: data.filename ?? formattedFile.fileName,
          bucket_name: data.bucket_name ?? formattedFile.bucket_name,
        },
      ]);
      writePodBucketAttachmentsCache(queryClient, shipmentId, (results) => [
        ...results.filter((existing) => existing.file_id !== data.file_id),
        data,
      ]);
      invalidatePodBucketAttachments();
    } catch (associateError) {
      if (isSyntheticOtherBucket(bucket)) {
        setValue("file_attachments", [...(getValues("file_attachments") ?? []), formattedFile]);
      } else {
        await deleteFile(formattedFile.reference).catch(() => undefined);
      }
      errorToast(associateError);
      setUploadProgress(0);
    }
  };

  const uploadFileHandler = (file: any) => {
    setUploadProgress(1);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("filename", file.name);

    uploadFile(formData, {
      onUploadProgress: (progressEvent: ProgressEvent) => {
        setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
      },
    })
      .then((res) => {
        if (res.status === 201) {
          const formattedFile = {
            attachment_id: bucket.id,
            file_id: res.data.id,
            fileName: res.data.name.split("/").pop(),
            url: res.data.url,
            reference: res.data.reference_number,
            size: file.size,
            bucket_name: bucket.bucket_name,
          };
          return handleAssociateAfterUpload(formattedFile);
        }
      })
      .catch((error) => {
        errorToast(error);
        setUploadProgress(0);
      });
  };

  const handleFileDelete = () => {
    if (!selectedFile || !selectedFile.reference) return;
    const { reference } = selectedFile;
    setIsDeleting(true);

    const deleteRequest = persistToPodBucket
      ? deletePodBucketAttachment(shipmentId, reference)
      : deleteFile(reference);

    deleteRequest
      .then((res) => {
        if (res.status === 204) {
          const updatedAttachments = fileAttachments.filter((item: any) => item.reference !== reference);
          setValue("file_attachments", updatedAttachments);
          if (persistToPodBucket) {
            writePodBucketAttachmentsCache(queryClient, shipmentId, (results) =>
              results.filter((existing) => existing.reference_number !== reference),
            );
            invalidatePodBucketAttachments();
          }
        } else
          throw new Error(
            intl.formatMessage({
              id: "general.deleteFileError",
              defaultMessage: "Could not delete file, please try again later",
            }),
          );
        setIsDeleting(false);
      })
      .catch((err) => {
        errorToast(
          err,
          intl.formatMessage({
            id: "general.deleteFileError",
            defaultMessage: "Could not delete file, please try again later",
          }),
        );
        setIsDeleting(false);
      });
  };

  return (
    <div className="mb-lg">
      <Checkbox
        disabled={isBucketMandatory}
        checked={isBucketMandatory ? true : Boolean(showAttachmentUploader)}
        name={attachmentFieldName}
        registerOptions={{
          onChange: (e: any) => {
            if (selectedFile && showAttachmentUploader && !e.target.checked) {
              setValue(attachmentFieldName, true);
            }
          },
        }}
        label={
          <>
            <div className={`${isBucketMandatory ? "required" : ""}`}>{bucket.bucket_name}</div>
            {!selectedFile && showAttachmentUploader && isSubmitted && !uploadProgress && isBucketMandatory && (
              <div className="text-error-primary text-sm">
                <FormattedMessage
                  id="components.molecules.top-nav.fileAttachmentRequired"
                  defaultMessage="File attachment is required"
                />
              </div>
            )}
          </>
        }
      />
      {showAttachmentSection && (
        <>
          {!uploadProgress && !selectedFile && !isAttachmentReadOnly && (
            <div className="mt-sm ml-3xl" id={`upload-btn-${bucket.id}`}>
              <Button
                onClick={handleUploadClick}
                className="position-relative"
                label={
                  <>
                    <div>
                      <FormattedMessage
                        id="tracking.components.uploadFileDialog.uploadedFile"
                        defaultMessage="Upload file"
                      />
                    </div>
                    <input
                      type="file"
                      accept="application/pdf, image/gif, image/tiff, text/csv, application/vnd.apple.pages, application/vnd.apple.keynote, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.presentationml.presentation, application/msword, application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.wordprocessingml.document, image/jpeg, image/png"
                      ref={hiddenFileInput}
                      className="hidden-file-input"
                      onChange={handleUploadChange}
                      onClick={(e: any) => {
                        e.target.value = null;
                      }}
                    />
                  </>
                }
                variant="secondary-color"
                iconLeft={<UploadIcon />}
              />
            </div>
          )}
          {uploadProgress > 0 && <UploadBar percentCompleted={uploadProgress} />}
          {!uploadProgress && selectedFile && (
            <div className="ml-3xl">
              <FilePreview
                fileName={selectedFile?.fileName}
                fileSize={
                  selectedFile.size == null ? undefined : formatFileSize(selectedFile.size)
                }
                url={selectedFile.url}
                onClose={isAttachmentReadOnly ? undefined : handleFileDelete}
                isLoading={isDeleting}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProofOfDeliveryAttachmentItem;
